import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UserGroupIcon,
  CalendarIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  UserPlusIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  ShoppingBagIcon,
  BellIcon,
  CreditCardIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioPlan } from "../hooks/useStudioPlan";
import { useHasUnseenUpdate } from "../utils/latestUpdate";
import NotificationPopup, {
  NotificationsNavButton,
} from "./NotificationPopup";
import dojoLogo from "../assets/dojotrack_logo.png";
import dojoLogoIconLight from "../assets/dojotrack_logo_icon_png.png";
import dojoLogoIconDark from "../assets/dojotrack_logo_icon_png_light.png";
import { isKioskMode } from "../utils/kioskAuth";

type NavSubItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type NavLinkItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permission: string;
};

type NavDropdownItem = {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permission: string;
  subItems: NavSubItem[];
};

type NavItem = NavLinkItem | NavDropdownItem;

function isNavDropdownItem(item: NavItem): item is NavDropdownItem {
  return "subItems" in item;
}

function isNavLinkItem(item: NavItem): item is NavLinkItem {
  return "href" in item;
}

type SidebarProps = Record<string, never>;

export default function Sidebar() {
  const location = useLocation();
  const {
    user,
    session,
    supabase,
    setSession,
    setUser,
    loading,
    hasPermission,
    logout,
  } = useAuth();
  const { plan, isFreePlan, loading: planLoading } = useStudioPlan();
  const hasUnseenUpdate = useHasUnseenUpdate(user?.id);
  const [studioInfoLoaded, setStudioInfoLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(true); // Sidebar collapsed by default on 2xl and below
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]); // Track which dropdowns are open

  useEffect(() => {
    setCollapsed(true); // Collapse sidebar on mount for 2xl and below
  }, []);

  useEffect(() => {
    const fetchStudioInfo = async () => {
      if (!session?.access_token) {
        setStudioInfoLoaded(true);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch studio info");
        }
        await response.json();
      } catch (error) {
        // Error fetching studio info - error handling without console logging
      } finally {
        setStudioInfoLoaded(true);
      }
    };

    fetchStudioInfo();
  }, [session]);

  // Auto-open Messaging dropdown if we're on any messaging sub-page
  useEffect(() => {
    if (
      location.pathname === "/app/sms/history" ||
      location.pathname === "/app/sms" ||
      location.pathname === "/app/messaging" ||
      location.pathname === "/app/push-notifications"
    ) {
      if (!openDropdowns.includes("Messaging")) {
        setOpenDropdowns((prev) => [...prev, "Messaging"]);
      }
    }
  }, [location.pathname]);

  // Don't render anything if we're still loading or in kiosk mode
  if (loading || isKioskMode() || !studioInfoLoaded) {
    return null;
  }

  // Don't render if no user (shouldn't happen in app layout, but safety check)
  if (!user) {
    return null;
  }

  // Additional check for specific routes that should never show sidebar
  const noSidebarRoutes = [
    "/app/onboarding",
    "/app/finishing-setup",
    "/app/student-sign-in",
    "/app/inactive-studio", // Hide regular sidebar for inactive studio flow
  ];

  if (noSidebarRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }

  const navigation: NavItem[] = [
    {
      name: "Dashboard",
      href: "/app",
      icon: HomeIcon,
      permission: "dashboard",
    },
    {
      name: "Students",
      href: "/app/students",
      icon: UserGroupIcon,
      permission: "students",
    },
    {
      name: "Staff",
      href: "/app/staff",
      icon: UserGroupIcon,
      permission: "staff",
    },
    {
      name: "Classes & Events",
      href: "/app/classes-events",
      icon: CalendarIcon,
      permission: "classes",
    },
    // {
    //   name: "Events",
    //   href: "/app/events",
    //   icon: CalendarIcon,
    //   permission: "events",
    // },
    {
      name: "Attendance",
      href: "/app/attendance",
      icon: ClipboardDocumentCheckIcon,
      permission: "attendance",
    },
    {
      name: "Student Sign-in",
      href: "/app/kiosk-mode",
      icon: UserPlusIcon,
      permission: "attendance", // Student sign-in is part of attendance
    },
    {
      name: "Messaging",
      icon: ChatBubbleLeftRightIcon,
      permission: "messaging",
      subItems: [
        {
          name: "Email",
          href: "/app/messaging",
          icon: EnvelopeIcon,
        },
        {
          name: "Compose SMS",
          href: "/app/sms",
          icon: ChatBubbleLeftIcon,
        },
        {
          name: "SMS History",
          href: "/app/sms/history",
          icon: ClockIcon,
        },
        {
          name: "Push Notification",
          href: "/app/push-notifications",
          icon: BellIcon,
        },
      ],
    },
    {
      name: "Leads",
      href: "/app/leads",
      icon: UserPlusIcon,
      permission: "leads",
    },
    {
      name: "Website Forms",
      href: "/app/forms",
      icon: DocumentTextIcon,
      permission: "forms",
    },
    {
      name: "Waivers",
      href: "/app/waivers",
      icon: ClipboardDocumentIcon,
      permission: "waivers",
    },
    {
      name: "Store",
      href: "/app/store",
      icon: ShoppingBagIcon,
      permission: "store",
    },
    {
      name: "Settings",
      href: "/app/settings",
      icon: Cog6ToothIcon,
      permission: "settings",
    },
  ];

  const toggleDropdown = (itemName: string) => {
    setOpenDropdowns((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName]
    );
  };

  const handleLogout = async () => {
    // Use the centralized logout function from AuthContext
    await logout();
  };

  return (
    <NotificationPopup>
      {/* Toggle Button (visible on 2xl and below) */}
      <button
        className="fixed top-4 left-4 z-50 2xl:hidden bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 shadow"
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
      >
        {collapsed ? (
          <Bars3Icon className="h-6 w-6 text-gray-900 dark:text-gray-100" />
        ) : (
          <XMarkIcon className="h-6 w-6 text-gray-900 dark:text-gray-100" />
        )}
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed z-40 top-0 left-0 h-full bg-gray-50 dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700
          transition-transform duration-200
          w-48
          ${collapsed ? "-translate-x-full" : "translate-x-0"}
          2xl:translate-x-0 2xl:static 2xl:block
        `}
        style={{ minHeight: "100vh" }}
      >
        <div className="flex flex-col w-48 h-screen min-h-0 bg-gray-50 dark:bg-gray-800">
          <div className="hidden 2xl:block flex-col items-start justify-center border-b border-gray-200 dark:border-gray-700 px-3 pt-3 pb-3 flex-shrink-0">
            {/* Show correct logo icon for light/dark mode */}
            <img
              src={dojoLogoIconLight}
              alt="DojoTrack Logo Icon"
              className="h-5 w-auto block dark:hidden"
            />
            <img
              src={dojoLogoIconDark}
              alt="DojoTrack Logo Icon"
              className="h-5 w-auto hidden dark:block"
            />
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto space-y-0.5 px-2 py-2 2xl:pt-2 pt-16">
            {navigation
              .filter((item) => hasPermission(item.permission))
              .map((item) => {
                if (isNavDropdownItem(item)) {
                  const isDropdownOpen = openDropdowns.includes(item.name);
                  const isAnySubItemActive = item.subItems.some(
                    (subItem) => location.pathname === subItem.href
                  );

                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => toggleDropdown(item.name)}
                        className={`group flex items-center justify-between w-full rounded-md px-2 py-1.5 text-sm font-medium ${
                          isAnySubItemActive
                            ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                            : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                        }`}
                      >
                        <div className="flex items-center">
                          <item.icon
                            className={`mr-2 h-4 w-4 flex-shrink-0 ${
                              isAnySubItemActive
                                ? "text-indigo-600 dark:text-indigo-400"
                                : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                            }`}
                            aria-hidden="true"
                          />
                          {item.name}
                        </div>
                        {isDropdownOpen ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                      {isDropdownOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.subItems.map((subItem) => {
                            const isSubItemActive = location.pathname === subItem.href;
                            return (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className={`group flex items-center rounded-md px-2 py-1.5 text-sm font-medium ${
                                  isSubItemActive
                                    ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                                    : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                                }`}
                                onClick={() => setCollapsed(true)}
                              >
                                <subItem.icon
                                  className={`mr-2 h-4 w-4 flex-shrink-0 ${
                                    isSubItemActive
                                      ? "text-indigo-600 dark:text-indigo-400"
                                      : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                                  }`}
                                  aria-hidden="true"
                                />
                                {subItem.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                if (!isNavLinkItem(item)) {
                  return null;
                }

                // Regular navigation item (no dropdown)
                const isActive = location.pathname === item.href;
                const isSettings = item.name === "Settings";
                const isUpgradeActive = location.pathname.startsWith("/app/settings/billing");
                
                return (
                  <React.Fragment key={item.name}>
                  {isSettings && isFreePlan && !planLoading && (
                    <Link
                      to="/app/settings/billing"
                      className={`group mb-1 flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-semibold shadow-sm ${
                        isUpgradeActive
                          ? "bg-indigo-700 text-white ring-2 ring-indigo-400 dark:ring-indigo-500"
                          : "bg-indigo-600 text-white hover:bg-indigo-500 ring-1 ring-indigo-400/60 dark:ring-indigo-500/60"
                      }`}
                      onClick={() => setCollapsed(true)}
                    >
                      <CreditCardIcon
                        className="mr-1 h-4 w-4 flex-shrink-0 text-white pointer-events-none"
                        aria-hidden="true"
                      />
                      <span className="flex-1 min-w-0 truncate pointer-events-none">Upgrade</span>
                    </Link>
                  )}
                  <Link
                    to={item.href}
                    className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium ${
                      isActive
                        ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
                        : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                    }`}
                    onClick={() => setCollapsed(true)} // Auto-collapse on nav click (mobile)
                  >
                    <item.icon
                      className={`mr-1 h-4 w-4 flex-shrink-0 ${
                        isActive
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                      }`}
                      aria-hidden="true"
                    />
                    <span className="flex-1 min-w-0 truncate">{item.name}</span>
                  </Link>
                    {/* Mobile-only utilities shown after Settings */}
                    {isSettings && user && (
                      <div className="xl:hidden space-y-1 mt-1">
                        <NotificationsNavButton
                          onNavigate={() => setCollapsed(true)}
                        />
                        {!planLoading && plan?.accessState === "paid" && (
                          <Link
                            to="/app/updates"
                            className={`group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium transition ${
                              hasUnseenUpdate
                                ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                                : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                            }`}
                            onClick={() => setCollapsed(true)}
                          >
                            <MegaphoneIcon
                              className={`mr-2 h-4 w-4 flex-shrink-0 ${
                                hasUnseenUpdate
                                  ? "text-indigo-600 dark:text-indigo-300"
                                  : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                              }`}
                              aria-hidden="true"
                            />
                            Updates
                            {hasUnseenUpdate && (
                              <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-300">
                                <span className="h-2 w-2 rounded-full bg-indigo-600 dark:bg-indigo-400" aria-hidden="true" />
                                New
                              </span>
                            )}
                          </Link>
                        )}
                        <Link
                          to="/app/report-bug"
                          className="group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 transition"
                          onClick={() => setCollapsed(true)}
                        >
                          <ExclamationTriangleIcon
                            className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                            aria-hidden="true"
                          />
                          Report a bug
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500 transition"
                        >
                          <ArrowRightOnRectangleIcon
                            className="mr-2 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400"
                            aria-hidden="true"
                          />
                          Logout
                        </button>
                        <div className="px-2 py-1 text-xs text-gray-400 dark:text-gray-500 text-center select-none">
                          © {new Date().getFullYear()} DojoTrack, LLC
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </nav>
          {/* Desktop-only utilities shown at the bottom */}
          {user && (
            <div className="hidden xl:block">
              <div className="px-2">
                <NotificationsNavButton />
                {!planLoading && plan?.accessState === "paid" && (
                <Link
                  to="/app/updates"
                  className={`group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium transition ${
                    hasUnseenUpdate
                      ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                      : "text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400"
                  }`}
                  onClick={() => setCollapsed(true)}
                >
                  <MegaphoneIcon
                    className={`mr-2 h-4 w-4 flex-shrink-0 ${
                      hasUnseenUpdate
                        ? "text-indigo-600 dark:text-indigo-300"
                        : "text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                    }`}
                    aria-hidden="true"
                  />
                  Updates
                  {hasUnseenUpdate && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-300">
                      <span className="h-2 w-2 rounded-full bg-indigo-600 dark:bg-indigo-400" aria-hidden="true" />
                      New
                    </span>
                  )}
                </Link>
                )}
                <Link
                  to="/app/report-bug"
                  className="group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 transition"
                  onClick={() => setCollapsed(true)}
                >
                  <ExclamationTriangleIcon
                    className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
                    aria-hidden="true"
                  />
                  Report a bug
                </Link>
              </div>
              <div
                className="border-t border-gray-200 dark:border-gray-700 p-3 flex-shrink-0"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              >
                <button
                onClick={handleLogout}
                className="group flex w-full items-center rounded-md px-2 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500 transition"
              >
                <ArrowRightOnRectangleIcon
                  className="mr-2 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400"
                  aria-hidden="true"
                />
                Logout
              </button>
                <div className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center select-none">
                  © {new Date().getFullYear()} DojoTrack, LLC
                </div>
                <div className="h-2"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay for collapsed sidebar on small screens */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 2xl:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}
    </NotificationPopup>
  );
}
